import { debounceTime, distinctUntilChanged, map, first, takeUntil, tap, filter, flatMap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TenantService, UserService } from '../services'
import { Location } from '@angular/common';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { Router } from '@angular/router';

@Component({
    template: `
        <div fxLayout="column" fxLayoutAlign="space-around stretch">
            <div *ngIf="!user" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
                <spinner></spinner>
            </div>
            <mat-card *ngIf="user" fxFlex="90" fxLayout="column" fxLayoutAlign="space-around stretch">
                <mat-card-header>
                    <mat-card-title><h2>Profile</h2></mat-card-title>
                </mat-card-header>
                <mat-card-content fxFlex="100" fxLayout="column" style="width:100%;" [formGroup]="form">
                    <mat-form-field>
                        <input matInput type="text" placeholder="First Name" formControlName="firstName" maxlength="100">
                        <mat-error *ngIf="form.get('firstName').hasError('required')">First Name cannot be blank</mat-error>
                    </mat-form-field>
                    <mat-form-field>
                        <input matInput type="text" placeholder="Last Name" formControlName="lastName" maxlength="100">
                        <mat-error *ngIf="form.get('lastName').hasError('required')">Last Name cannot be blank</mat-error>
                    </mat-form-field>
                    <mat-form-field>
                        <input matInput type="text" placeholder="Email Address" formControlName="emailAddress" maxlength="256">
                        <mat-error *ngIf="form.get('emailAddress').hasError('required')">Email Address is required.</mat-error>
                        <mat-error *ngIf="form.get('emailAddress')?.hasError('pattern')">Email address must be valid.</mat-error>
                        <mat-error *ngIf="form.get('emailAddress')?.hasError('validateUniqueEmail')">Email address must be unique.</mat-error>
                    </mat-form-field>
                    <div *ngIf="allowPasswordChanges">
                        <mat-form-field style="width:100%">
                            <input matInput type="password" placeholder="Current Password" formControlName="currentPassword">
                            <mat-error *ngIf="form.get('currentPassword').hasError('required')">Current Password cannot be blank</mat-error>
                            <mat-error *ngIf="form.get('currentPassword').hasError('validateCurrentPassword')">Current Password is incorrect</mat-error>
                        </mat-form-field>
                    </div>
                    <div *ngIf="allowPasswordChanges">
                        <mat-form-field style="width:100%">
                            <input matInput type="password" placeholder="New Password" formControlName="newPassword" name="newPassword" autocomplete="new-password">
                            <mat-error *ngIf="form.get('newPassword')?.hasError('pattern')">Password must contain at least one upper case letter, one lowercase letter, one number and be at least 8 characters long.</mat-error>
                        </mat-form-field>
                    </div>
                    <div *ngIf="allowPasswordChanges">
                        <mat-form-field style="width:100%">
                            <input matInput type="password" placeholder="Confirm New Password" formControlName="confirmPassword" name="confirmPassword" autocomplete="new-password">
                            <mat-error *ngIf="form.get('confirmPassword')?.hasError('noMatch')">Passwords must match.</mat-error>
                        </mat-form-field>
                    </div>
                    <div>
                        <h3>Microsoft Teams</h3>
                        <mat-form-field >
                            <input matInput placeholder="Teams Key" formControlName="teamsKey">
                            <button mat-icon-button matSuffix (click)="generateTeamsToken()">
                                <mat-icon>autorenew</mat-icon>
                            </button>
                        </mat-form-field>
                    </div>
                </mat-card-content>
                <mat-card-actions>
                    <button mat-raised-button 
                            color="primary" 
                            (click)="save()" 
                            [disabled]="validatingCurrentPassword || form.pristine || form.invalid">Save</button>
                </mat-card-actions>
            </mat-card>
            <mat-card *ngIf="allowSelfDelete">
                <mat-card-actions>
                    <div>
                        <button mat-raised-button color="primary" (click)="delete()">Delete Account</button>    
                    </div>
                </mat-card-actions>
            </mat-card>
        </div>
    `
})
export class ProfileComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    form: FormGroup;
    user: any;
    allowPasswordChanges: boolean = false;
    hostname: any;
    validatingCurrentPassword: boolean;
    allowSelfDelete: boolean;

    constructor(private dialog: MatDialog,
                private tenantService: TenantService,
                private userService: UserService,
                public location: Location, 
                public snackBar: MatSnackBar,
                private fb: FormBuilder,
                private router: Router) {}
    
    ngOnInit(): void {
        this.user = this.userService.getCurrentUser();
        
        this.form = this.fb.group({
            firstName: [this.user.firstName, Validators.required],
            lastName: [this.user.lastName, Validators.required],
            emailAddress: [this.user.emailAddress, 
                           [Validators.required, Validators.pattern('[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$')],
                           this.uniqueEmail.bind(this)],
            currentPassword: [''],
            newPassword: ['', [Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$')]],
            confirmPassword: ['', this.passwordsMatch.bind(this)],    
            teamsKey: [{ value: this.user.teamsKey, disabled: true }]           
        });

        this.tenantService.getCurrentTenant().pipe(first())
        .subscribe(tenant => {
            this.hostname = tenant.hostName; 
            this.allowPasswordChanges = !tenant.settings.samlEntryPoint
            this.allowSelfDelete = tenant.settings.publicTopics === 'true';
            if (!this.allowPasswordChanges) {
                this.form.get('emailAddress').disable();
            }
        });
        
        this.form.get('newPassword').valueChanges.pipe(takeUntil(this.cleanupTime))
        .subscribe(value => {
            const currentPasswordControl = this.form.get('currentPassword');
            currentPasswordControl.setValidators(value.length ? [Validators.required] : []);
            currentPasswordControl.setAsyncValidators(value.length ? [this.validateCurrentPassword.bind(this)] : []);
            currentPasswordControl.markAsTouched();
            currentPasswordControl.updateValueAndValidity();
            this.form.get('confirmPassword').updateValueAndValidity();
        });
    }

    passwordsMatch(c: AbstractControl): { noMatch: boolean } {
        if (this.form) {
            const newPasswordCtrl = this.form.get('newPassword');
            const confPasswordCtrl = this.form.get('confirmPassword');
            if (newPasswordCtrl.value !== confPasswordCtrl.value && (newPasswordCtrl.value.length || confPasswordCtrl.value.length)) {
                return { noMatch: true};
            }
        }
    }

    validateCurrentPassword(control: AbstractControl): Observable<{ [key: string]: any }> {
        this.validatingCurrentPassword = true;
        return this.userService.validatePassword(control.value)
        .pipe(
            debounceTime(250),
            distinctUntilChanged(),
            tap(() => this.validatingCurrentPassword = false),
            map(result => result.isValid ? null : { validateCurrentPassword: true })
        );
    }

    uniqueEmail(control: AbstractControl): Observable<{ [key: string]: any }> {
        return this.userService.getUserByEmail(control.value, this.hostname)
        .pipe(
            debounceTime(250),
            distinctUntilChanged(),
            map(user => user.id === this.user.id ? undefined : { validateUniqueEmail: true })
        );
    }

    delete(): void {
        this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete your account?`,
                content: `This will permanently remove your account.`,
                actionLabel: 'Delete'}
            }).afterClosed()
            .pipe(
                filter(doIt => doIt),
                flatMap(() => this.userService.deleteUser(this.user.id)),
                flatMap(() => this.userService.signOut()),
                tap(() => this.snackBar.open(`Your account has been deleted.`, 'OK', { duration: 3000}))
            ).subscribe(() => this.router.navigate(['/login']));
    }

    get teamsKeyIsEmpty(): boolean {
        return !this.form.getRawValue().teamsKey?.trim().length;
    }

    /**
     * 
     * @param mask eg '00000-00000-00000-00000-00000'
     * @returns Serial Number, based on a certain mask
     */
    generateSerialNumber(mask = '') {
        let serialNumber = '';

        for (let i = 0; i < mask.length; i++) {
            const maskChar = mask[i];
            const chars = '1234567890ABCDEFGIJKLMNOPQRSTUVWXYZ';
            const rando = chars[Math.floor(Math.random() * chars.length)];
            serialNumber += maskChar === '0' ? rando : maskChar;
        }

        return serialNumber;
    }

    generateTeamsToken(): void {
        const teamsKeyField = this.form.get('teamsKey');
        if (this.teamsKeyIsEmpty) {
            teamsKeyField.setValue(this.generateSerialNumber('0000-0000-0000-0000'));
            teamsKeyField.markAsDirty();
        } else {
            this.dialog.open(ConfirmationDialogComponent, { 
                data: {
                    title: `Regenerate your Teams Key?`,
                    content: `You will need to update the key in Microsoft Teams to edit your connector settings.`,
                    actionLabel: 'Regenerate'
                }
            }).afterClosed().pipe(
                filter(doIt => doIt),
                tap(() => teamsKeyField.setValue(this.generateSerialNumber('0000-0000-0000-0000')))
            ).subscribe(() => teamsKeyField.markAsDirty());
        }
    }

    save = () => this.userService.updateCurrentUser({ 
            firstName: this.form.get('firstName').value, 
            lastName: this.form.get('lastName').value, 
            emailAddress: this.form.get('emailAddress').value, 
            password: this.form.get('newPassword').value || undefined,
            teamsKey: this.teamsKeyIsEmpty ? undefined : this.form.getRawValue().teamsKey,
        }).subscribe(result => {
            this.user = result;
            this.form.get('currentPassword').setValue('');
            this.form.get('newPassword').setValue('');
            this.form.get('confirmPassword').setValue('');
            this.form.markAsPristine();
            this.snackBar.open('Profile updated.', 'OK', { duration: 3000});            
        });

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}
